import { z } from 'zod';
import { isoDateStringSchema, uuidSchema } from './common';

export const listTypeSchema = z.enum(['shopping', 'todo', 'notes']);
export type ListType = z.infer<typeof listTypeSchema>;

// Calendar date in YYYY-MM-DD form (no time component). Used for list-level
// planning fields such as `plannedDate`. Storing as a date-only string keeps
// timezone semantics simple — the user plans for a specific calendar day.
export const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a calendar date in YYYY-MM-DD form');
export type CalendarDate = z.infer<typeof calendarDateSchema>;

// Title der Default-Liste, in der die Tracking→Todo-Pipeline (#143/#144/#145)
// automatisch erzeugte Todos ablegt. Auch im Frontend genutzt, um diese Liste
// als „auto" zu markieren — daher hier in shared-types statt nur im API-Service.
export const PACKAGES_AUTO_TODO_LIST_TITLE = 'Pakete';

const shoppingItemDataSchema = z.object({
  type: z.literal('shopping'),
  title: z.string().min(1).max(200),
  checked: z.boolean(),
  missing: z.boolean().default(false),
  quantity: z.number().int().min(1),
  description: z.string().max(2000).optional(),
  departmentId: uuidSchema.nullable().optional(),
});

const todoPrioritySchema = z.enum(['low', 'normal', 'high']);
const todoItemDataSchema = z.object({
  type: z.literal('todo'),
  title: z.string().min(1).max(200),
  done: z.boolean(),
  dueDate: isoDateStringSchema.nullable().optional(),
  priority: todoPrioritySchema.optional(),
});

const notesItemDataSchema = z.object({
  type: z.literal('notes'),
  title: z.string().min(1).max(200),
  body: z.string().max(10_000),
});

export const listItemDataSchema = z.discriminatedUnion('type', [
  shoppingItemDataSchema,
  todoItemDataSchema,
  notesItemDataSchema,
]);
export type ListItemData = z.infer<typeof listItemDataSchema>;

export const listItemSchema = z.object({
  id: uuidSchema,
  listId: uuidSchema,
  position: z.number().int().nonnegative(),
  data: listItemDataSchema,
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type ListItem = z.infer<typeof listItemSchema>;

export const listSchema = z.object({
  id: uuidSchema,
  ownerSub: z.string().min(1),
  type: listTypeSchema,
  title: z.string().min(1).max(120),
  storeId: uuidSchema.nullable().optional(),
  plannedDate: calendarDateSchema.nullable().optional(),
  position: z.number().int().nonnegative(),
  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type List = z.infer<typeof listSchema>;

// Reorder all lists of a single owner. The client sends every list ID it
// currently knows in the new desired order; the server writes `position`
// 0..n-1 in that order in a single transaction. Lists missing from the array
// keep their previous position (used to be defensive — clients normally pass
// the full set after fetching).
export const reorderListsDtoSchema = z.object({
  orderedIds: z.array(uuidSchema).min(1),
});
export type ReorderListsDto = z.infer<typeof reorderListsDtoSchema>;

export const listWithItemsSchema = listSchema.extend({
  items: z.array(listItemSchema),
});
export type ListWithItems = z.infer<typeof listWithItemsSchema>;

export const createListDtoSchema = z.object({
  type: listTypeSchema,
  title: z.string().min(1).max(120),
  storeId: uuidSchema.nullable().optional(),
  plannedDate: calendarDateSchema.nullable().optional(),
});
export type CreateListDto = z.infer<typeof createListDtoSchema>;

export const updateListDtoSchema = z.object({
  title: z.string().min(1).max(120).optional(),
  storeId: uuidSchema.nullable().optional(),
  plannedDate: calendarDateSchema.nullable().optional(),
});
export type UpdateListDto = z.infer<typeof updateListDtoSchema>;

// Item create/update DTOs carry the type-specific payload minus fields that the
// server owns (position defaults, timestamps). The `data` discriminator must
// match the parent list's type — this is enforced in the service layer.
const shoppingItemCreateSchema = shoppingItemDataSchema.extend({
  checked: z.boolean().default(false),
  missing: z.boolean().default(false),
  quantity: z.number().int().min(1).default(1),
});
const todoItemCreateSchema = todoItemDataSchema.extend({
  done: z.boolean().default(false),
});
const notesItemCreateSchema = notesItemDataSchema.extend({
  body: z.string().max(10_000).default(''),
});

export const createListItemDataSchema = z.discriminatedUnion('type', [
  shoppingItemCreateSchema,
  todoItemCreateSchema,
  notesItemCreateSchema,
]);
export type CreateListItemData = z.infer<typeof createListItemDataSchema>;

export const createListItemDtoSchema = z.object({
  data: createListItemDataSchema,
  position: z.number().int().nonnegative().optional(),
});
export type CreateListItemDto = z.infer<typeof createListItemDtoSchema>;

export const updateListItemDtoSchema = z
  .object({
    data: listItemDataSchema.optional(),
    position: z.number().int().nonnegative().optional(),
  })
  .refine((v) => v.data !== undefined || v.position !== undefined, {
    message: 'At least one of data or position must be provided',
  });
export type UpdateListItemDto = z.infer<typeof updateListItemDtoSchema>;

// Transfer all items with `missing: true` from a shopping list into another
// list (existing or newly created). Exactly one of targetListId / newListTitle
// must be supplied. The source items remain unchanged; copies are inserted in
// the target with `missing` and `checked` reset to false.
export const transferMissingItemsDtoSchema = z
  .object({
    targetListId: uuidSchema.optional(),
    newListTitle: z.string().min(1).max(120).optional(),
  })
  .refine(
    (v) =>
      (v.targetListId !== undefined && v.newListTitle === undefined) ||
      (v.targetListId === undefined && v.newListTitle !== undefined),
    { message: 'Exactly one of targetListId or newListTitle must be provided' },
  );
export type TransferMissingItemsDto = z.infer<typeof transferMissingItemsDtoSchema>;
